import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { REQUEST_CATEGORY_SINGLE_FIELD_API } from '../../../constants/api'
import { SUBMENU_DEFAULT_VALUE } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useNotification,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { getEmptyPermanentFilters } from '../../../utils/filter'
import Button from '../../Button'
import { CategoryTableLayout, useCategoryTablePage } from '../../Category'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'
import {
  getEntityTabFilterStateApi,
  updateEntityTabTableConfig,
} from '../EntityTab.api'
import { getEntityTabFilterBarApiUrl } from '../EntityTab.utils'

import { ENTITY_TAB_CATEGORIES_DEFAULT_QA_NAME } from './EntityTabCategories.constants'
import { getEntityTabCategoriesScopeFilter } from './EntityTabCategories.utils'

import * as styles from './EntityTabCategories.module.css'

import CategoriesIcon from '../../../icons/configuration.svg'
import EditIcon from '../../../icons/edit.svg'

function EntityTabCategories({
  EditModal = null,
  canEdit = null,
  editButtonTextKey = 'Category.EditCategoriesBtnKey',
  entity,
  extraListParams = null,
  qaName = ENTITY_TAB_CATEGORIES_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  onClickEdit = null,
  onRegisterReload,
  onReloadCounters,
  onSave,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const [isSavingCategories, setSavingCategories] = useState(false)

  function handleCloseModal() {
    setModal(null)
  }

  async function handleApplyTableConfig(data) {
    return updateEntityTabTableConfig(data, entity.plural)
  }

  function getListApiParams(params) {
    return { ...params, ...extraListParams }
  }

  const tablePage = useCategoryTablePage({
    asyncTableOptions: {
      extraFilters: getEntityTabCategoriesScopeFilter({
        scopeFilterKey,
        scopeId,
      }),
      filterStateApi: getEntityTabFilterStateApi({
        entityPlural: entity.plural,
        objectId: scopeId,
      }),
      getListApiParams,
      withoutSubMenu: true,
    },
    entity,
    onApplyTableConfig: handleApplyTableConfig,
  })

  const {
    availableFilters,
    filterValues,
    handleChangeShouldReloadFilters,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    shouldReloadFilters,
  } = tablePage

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReloadTableAfterMassAction,
    onReloadCounters,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  function handleSubmitEditCategories({ categoryIds }) {
    handleCloseModal()
    setSavingCategories(true)

    onSave({ categoryIds })
      .then(() => {
        handleReloadTableAfterMassAction()
        handleAddSuccessNotification('Notification.CommonTitleChangedKey')
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setSavingCategories(false)
      })
  }

  function handleOpenEditCategories() {
    setModal(
      <EditModal
        scopeId={scopeId}
        onClose={handleCloseModal}
        onSubmit={handleSubmitEditCategories}
      />
    )
  }

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  const handleClickEdit = onClickEdit ?? handleOpenEditCategories

  const hasCategories = items?.items?.length > 0

  const shouldCollapseToolbar =
    !hasCategories && !hasFilterChanged && !isLoading

  const canEditCategories =
    Boolean(EditModal || onClickEdit) &&
    (canEdit ?? (permissions.can_edit || isLoading))

  const emptyStateActions = getEmptyStateActions({
    Icon: EditIcon,
    actionName: qaName.editCategories,
    isLoading,
    qaName: qaName.editCategories,
    shouldShowActions: canEditCategories && !hasFilterChanged,
    text: t(editButtonTextKey),
    onClick: handleClickEdit,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        {...getQaSelector({ value: qaName.table })}
        className={styles.EntityTabCategories}
        ref={pageScrollWrapperRef}
      >
        {isRefetching || isSavingCategories ? <PageLoader /> : null}
        {shouldCollapseToolbar ? null : (
          <div className={styles.toolbar}>
            <FilterBar
              responsive
              apiUrl={getEntityTabFilterBarApiUrl({
                baseApi: REQUEST_CATEGORY_SINGLE_FIELD_API,
                scopeFilterKey,
                scopeId,
              })}
              availableFilters={availableFilters}
              className={styles.filterBar}
              entity={entity}
              filterTriggerVariant={
                FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
              }
              filterValues={filterValues}
              getPermanentFilters={getEmptyPermanentFilters}
              isSkeleton={isLoading}
              objectId={scopeId}
              ordering={ordering}
              pagination={{ per_page: pagination?.pageSize }}
              setFilterValues={handleSetFilterValues}
              shouldReloadValues={shouldReloadFilters}
              sub={SUBMENU_DEFAULT_VALUE}
              onChangeShouldReloadValues={handleChangeShouldReloadFilters}
              onReload={handleReload}
            />
            <PageActions className={styles.actions} isLoading={isLoading}>
              {canEditCategories ? (
                <Button.Text
                  beforeIconProps={{ Icon: EditIcon }}
                  isSkeleton={isLoading}
                  qaName={qaName.editCategories}
                  text={t(editButtonTextKey)}
                  onClick={handleClickEdit}
                />
              ) : null}
            </PageActions>
          </div>
        )}
        <CategoryTableLayout
          emptyState={
            <PageEmptyState
              EntityIcon={CategoriesIcon}
              actions={emptyStateActions}
              text={t('Category.NoDataKey')}
            />
          }
          hasTable={hasCategories || isLoading}
          tablePage={tablePage}
        />
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
        {modal}
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabCategories
